import { ButtonCusWithIcon, ButtonSearch } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import {
    Form,
    FormControl,
    FormField,
    FormItem
} from '@/components/common/Form';
import Table from '@/components/table/Table';
import {
    DEFAULT_SELECT_PAGE_SIZES,
    REGISTER_STATUS_LIST
} from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { COMMON_TYPE, LANGUAGES_SUPPORTED, REGISTER_STATUS, ROLE_USER } from '@/types/enums';
import { BikesLocation } from '@/types/location';
import { isPathEk } from '@/utils/routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

export default observer(function BikesRegistedLocation() {
    // hook
    const { t, i18n: { language } } = useTranslation();
    const { id } = useParams();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        bikesLocationStore: {
            bikeRegisterLocations,
            totalRecordBikeRegisterLocation,
            defaultPagingBikeRegisterLocation,
            pagingBikeRegisterLocation,
            keepSearchParamsRegisterLocations,
            getBikeByLocation,
            registerBike,
            unregisterBike,
            setObservable,
            clean
        },
        bikeStore: {
            getListModelName, bikeModelList
        },
        locationStore: { location },
        accountStore: { accounts, getAccounts, defaultPaging: defaultPagingAccount },
        capacitieStore: { capacitieList, getCapacitieList },
        generalStore: { manufacturers, getManufacturers },
        modalStore: { showAlertModal }
    } = useStore();

    // const
    const newId = id ?? location?.id;
    // state
    const [rowSelected, setRowSelected] = useState({});
    const [enableStatus, setEnableStatus] = useState<boolean>(false);

    // validate schema
    const validateSchema = yup.object().shape({
        isRegis: yup.boolean().optional().nullable(),
        bikeManufacture: yup.string(),
        bikeCapacityId: yup.number(),
        bikeModel: yup.string(),
        bikeOwnerId: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const checkValueStatusRegister = (value: any): boolean => {
        if (value === Boolean(REGISTER_STATUS.UNREGISTER) || value === Boolean(REGISTER_STATUS.REGISTERED)) {
            return true;
        }
        return false;
    };
    const checkValueStringStatusRegister = (value: any): boolean => {
        if (value === 'true' || value === 'false') {
            return true;
        }
        return false;
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            isRegis: checkValueStringStatusRegister(keepSearchParamsRegisterLocations?.isRegis?.toString()) ? keepSearchParamsRegisterLocations?.isRegis : null,
            bikeManufacture: keepSearchParamsRegisterLocations?.bikeManufacture ?? undefined,
            bikeCapacityId: keepSearchParamsRegisterLocations?.bikeCapacityId ?? undefined,
            bikeModel: keepSearchParamsRegisterLocations?.bikeModel ?? undefined,
            bikeOwnerId: keepSearchParamsRegisterLocations?.bikeOwnerId ?? undefined
        }
    });

    // life cycle
    useEffect(() => {
        // fetch engine size
        getCapacitieList({
            type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS,
            page: -1
        });

        // fetch Manufacturer
        getManufacturers();

        // fetch bike list
        onFetchBikes(keepSearchParamsRegisterLocations, pagingBikeRegisterLocation);

        getListModelName();

        getAccounts({ isOwner: true }, { ...defaultPagingAccount, pagination: { ...defaultPagingAccount.pagination, pageIndex: -2 } });

        return () => {
            clean();
        };
    }, []);

    // columns
    const columns: ColumnDef<BikesLocation>[] = [
        {
            accessorKey: 'bikeImage',
            header: '',
            size: 120,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[110px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => {
                const imgUrl = original?.vehicleImageRight?.filePath ||
                    original?.vehicleImageLeft?.filePath ||
                    original?.vehicleImageBefore?.filePath ||
                    original?.vehicleImageAfter?.filePath;
                return (
                    <div className=''>
                        {imgUrl &&
                            <img className='max-w-[70px] h-[50px] object-contain' src={imgUrl ?? ''} alt={t('field.bike_image')} />}
                    </div>
                );
            }
        },
        {
            accessorKey: 'bikeModel',
            header: t('field.model_name'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[120px] break-all line-clamp-2'>{original?.owner ? original?.bikeModel : ''}</div>
            )
        },
        {
            accessorKey: 'manufacture',
            header: t('field.manufacture'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <span>
                    {
                        manufacturers.find(
                            manufacturer =>
                                manufacturer.id.toString() === original.manufacture
                        )?.name_jp
                    }
                </span>
            )
        },
        ...(isEk ?
            [] :
            [
                {
                    accessorKey: 'capacity',
                    header: t('field.engine_size'),
                    size: 200,
                    enableSorting: false,
                    cell: ({ row: { original } }) => (
                        <span>{t(`words_title.${original.capacity?.code}`)}</span>
                    )
                }
            ]),
        {
            accessorKey: 'owner',
            header: t('field.owner'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[100px] break-all line-clamp-2'>{original?.owner ? original?.owner.fullname : ''}</div>
            )
        },
        {
            accessorKey: 'owner.userCode',
            header: t('field.user_code'),
            size: 150,
            meta: {
                headerClassName: '',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <p className='break-all'>{original.owner?.userCode ?? ''}</p>
            ),
            enableSorting: false
        },
        {
            id: 'actions',
            accessorKey: 'action',
            // header: t('field.action'),
            header: '',
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) =>
                original.isRegis ?
                    (
                        <ButtonCusWithIcon
                            type='button'
                            onClick={() => handleClickUnregister(false, Number(original.id))}
                            variant='outline'
                            size='xsm'
                            iconId='#icon-unregister'
                            widthIcon={20}
                            heightIcon={20}
                        >
                            {t('button.unregister')}
                        </ButtonCusWithIcon>
                    ) :
                    (
                        <ButtonCusWithIcon
                            type='button'
                            onClick={() => handleClickRegister(false, Number(original.id))}
                            variant='outlineOrange3'
                            size='xsm'
                            iconId='#icon-register'
                            widthIcon={24}
                            heightIcon={16}
                        >
                            {t('button.register')}
                        </ButtonCusWithIcon>
                    )
        }
    ];

    // function
    const onSetEnableStatus = (valueIsRegis: any) => {
        if (checkValueStringStatusRegister(valueIsRegis?.toString())) {
            setEnableStatus(true);
        } else {
            setEnableStatus(false);
        }
    };

    const onSearch = (searchParams?: any) => {
        onSetEnableStatus(searchParams?.isRegis);

        setRowSelected({});
        onFetchBikes(searchParams ?? {}, defaultPagingBikeRegisterLocation);
        searchParams && setObservable('keepSearchParamsRegisterLocations', toJS(searchParams));
    };

    const onFetchBikes = async (searchParams?: any, paging?: TableState) => {
        if (!newId) return;
        const res = await flowResult(getBikeByLocation({ locationId: +newId, ...searchParams }, paging));
        if (res) {
            onSetEnableStatus(searchParams?.isRegis);
        }
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        if (!newId) return;
        const newPaging =
            stateUpdater instanceof Function ?
                stateUpdater(pagingBikeRegisterLocation) :
                stateUpdater;
        onFetchBikes(keepSearchParamsRegisterLocations, newPaging);
    };

    const handleClickRegister = async (isBikeArray: boolean = true, bikeId?: number) => {
        if (!newId) return;

        let bikeIds: number[] = [];
        if (isBikeArray) {
            bikeIds = Object.keys(rowSelected).map(item => Number(item));
        } else if (bikeId) {
            bikeIds.push(bikeId);
        }

        const res = await flowResult(registerBike({ locationId: +newId, bikeIds }));
        if (res) {
            showAlertModal({
                id: 'success',
                type: 'success',
                content: t('messages.register_success'),
                onClickXIcon: () => {
                    setRowSelected({});
                    onFetchBikes(keepSearchParamsRegisterLocations, pagingBikeRegisterLocation);
                }
            });
        }
    };

    const handleClickUnregister = async (isBikeArray: boolean = true, bikeId?: number) => {
        if (!newId) return;

        let bikeIds: number[] = [];
        if (isBikeArray) {
            bikeIds = Object.keys(rowSelected).map(item => Number(item));
        } else if (bikeId) {
            bikeIds.push(bikeId);
        }

        const res = await flowResult(
            unregisterBike({ locationId: +newId, bikeIds })
        );
        if (res) {
            showAlertModal({
                id: 'success',
                type: 'success',
                content: t('messages.unregister_success'),
                onClickXIcon: () => {
                    setRowSelected({});
                    onFetchBikes(keepSearchParamsRegisterLocations, pagingBikeRegisterLocation);
                }
            });
        }
    };

    const isDisabledUnregister = useMemo((): boolean => {
        if (keepSearchParamsRegisterLocations?.isRegis === Boolean(REGISTER_STATUS.UNREGISTER)) {
            return true;
        }
        return Object.keys(rowSelected).length === 0;
    }, [keepSearchParamsRegisterLocations?.isRegis, enableStatus, rowSelected]);

    const isDisabledRegister = useMemo((): boolean => {
        if (keepSearchParamsRegisterLocations?.isRegis === Boolean(REGISTER_STATUS.REGISTERED)) {
            return true;
        }
        return Object.keys(rowSelected).length === 0;
    }, [keepSearchParamsRegisterLocations?.isRegis, enableStatus, rowSelected]);

    const renderButtonTable = () => {
        return (
            <>
                <ButtonCusWithIcon
                    type='button'
                    variant='outline'
                    size='md'
                    disabled={isDisabledUnregister}
                    onClick={() => handleClickUnregister(true)}
                    className='w-full md:h-12 sm:w-44 point-900:w-48'
                    iconId='#icon-unregister'
                    widthIcon={20}
                    heightIcon={20}
                >
                    {t('button.unregister')}
                </ButtonCusWithIcon>
                <ButtonCusWithIcon
                    type='button'
                    variant='outlineOrange3'
                    size='md'
                    disabled={isDisabledRegister}
                    onClick={() => handleClickRegister(true)}
                    className='w-full md:h-12 sm:w-44 point-900:w-48'
                    iconId='#icon-register'
                    widthIcon={24}
                    heightIcon={16}
                >
                    {t('button.register')}
                </ButtonCusWithIcon>
            </>
        );
    };

    return (
        <div className='w-full layout-card flex flex-col gap-4 md:gap-6'>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='grid grid-cols-1 point-375:grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 lg:gap-4 xl:gap-6'>
                        <FormField
                            control={form.control}
                            name='bikeManufacture'
                            render={({ field }) => (
                                <FormItem>
                                    <Combobox
                                        placeholder={t('field.manufacture')}
                                        value={field.value ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.code);
                                        }}
                                        items={
                                            Array.isArray(manufacturers) && manufacturers?.length ?
                                                [
                                                    ...manufacturers.map(item => ({
                                                        label: language === LANGUAGES_SUPPORTED.Japanese ? item.name_jp : item.name,
                                                        value: item.id.toString(),
                                                        code: item.id
                                                    }))
                                                ] :
                                                []
                                        }
                                    />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='bikeModel'
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.model_name')}
                                            value={field.value ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.value ?? '');
                                            }}
                                            search={true}
                                            items={
                                                (Array.isArray(bikeModelList) && bikeModelList?.length > 0 ?
                                                    [
                                                        ...bikeModelList.map(item => ({
                                                            label: item.bikeModel,
                                                            value: item.bikeModel,
                                                            code: item.bikeModel
                                                        }))
                                                    ] :
                                                    []
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='bikeCapacityId'
                            render={({ field }) => (
                                <FormItem>
                                    <Combobox
                                        placeholder={t('field.engine_size')}
                                        value={field.value?.toString() ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.code);
                                        }}
                                        items={
                                            Array.isArray(capacitieList) && capacitieList?.length ?
                                                [
                                                    ...capacitieList
                                                        .filter(item => item.id && item.name)
                                                        .map(item => ({
                                                            label: t(`words_title.${item.code}`) ?? '',
                                                            value: item.id.toString(),
                                                            code: item.id
                                                        }))
                                                ] :
                                                []
                                        }
                                    />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='isRegis'
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.status')}
                                            value={field.value?.toString() ?? ''}
                                            onChange={(item) => {
                                                field.onChange(checkValueStatusRegister(item?.code) ? item.code : null);
                                            }}
                                            items={REGISTER_STATUS_LIST.map(item => ({
                                                label: `${t(item.name)}`,
                                                value: item.value.toString(),
                                                code: item.value
                                            }))}
                                            search={false}
                                            disableClearIcon
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='bikeOwnerId'
                            render={({ field }) => (
                                <FormItem className='col-span-full sm:col-span-1'>
                                    <Combobox
                                        placeholder={t('field.owner')}
                                        value={field.value?.toString() ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.code ?? '');
                                        }}
                                        items={
                                            (Array.isArray(accounts) && accounts?.length ?
                                                [
                                                    ...accounts
                                                        .filter(account => (
                                                            Number(account?.role?.id) !== ROLE_USER.RENTAL_BIKE_OWNER_EK &&
                                                            Number(account?.role?.id) !== ROLE_USER.RENTAL_BIKE_OWNER_RD
                                                        ))
                                                        .map(item => ({
                                                            label: item.fullname,
                                                            labelSearch: item.fullname + item?.userCode,
                                                            value: item.id.toString(),
                                                            code: item.id
                                                        }))
                                                ] :
                                                []
                                            )
                                        }
                                    />
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className='w-full mt-4 flex justify-center sm:grid sm:grid-cols-3 xl:grid-cols-5 gap-3 lg:gap-4 xl:gap-6'>
                        <div className='w-full sm:w-auto sm:col-start-2 xl:col-start-3 flex justify-center'>
                            <ButtonSearch
                                size='md'
                                className='w-full max-w-52'
                            >
                                {t('button.search')}
                            </ButtonSearch>
                        </div>
                    </div>
                </form>
            </Form>

            <div className='flex justify-center w-full xsm:gap-4 gap-2 sm:hidden'>
                {renderButtonTable()}
            </div>

            <Table
                columns={columns}
                data={bikeRegisterLocations}
                state={{
                    ...pagingBikeRegisterLocation,
                    rowSelection: rowSelected
                }}
                rowCount={totalRecordBikeRegisterLocation}
                onStateChange={onStateChange}
                selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                getRowId={row => row.id?.toString()}
                enableRowSelection={({ original }) => (enableStatus)}
                onRowSelectionChange={e => setRowSelected(e)}
                showSelectPageSize={true}
                renderActions={(
                    <div className='sm:flex hidden sm:gap-4 gap-2'>
                        {renderButtonTable()}
                    </div>
                )}
            />
        </div>
    );
});
